import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  const headers: Record<string, string> = data && !isFormData ? { "Content-Type": "application/json" } : {};

  try {
    // Attach Bearer token for admin endpoints when available
    if (url.startsWith('/api/admin')) {
      const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // Safeguard for environments without localStorage
  }

  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? data as FormData : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = String(queryKey[0]);
    const headers: Record<string, string> = {};

    try {
      // Automatically include admin token for protected admin routes
      if (url.startsWith('/api/admin')) {
        const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Ignore if localStorage is not accessible
    }

    const res = await fetch(url, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
